import currencyFormatter from 'currencyformatter.js';

const currencyFormatters = {
	EUR: {
		postFormat() {
			const currencySymbol = currencyFormatter.symbols.EUR;
			const symbolLen = currencySymbol.length;
			const pattern = new RegExp(`(\\s)+${currencySymbol}`, 'g');
			let { value } = this;
			if (this.locale === 'fi_FI' || this.locale === 'fi') {
				value = this.value.replace(pattern, currencySymbol);
			} else if (this.value.substr(-symbolLen, symbolLen) === currencySymbol) {
				value = this.value.replace(pattern, currencySymbol);
				if (value.substr(0, 1) === '-') {
					value = value.substr(1);
					value = `-${currencySymbol}${value.replace(currencySymbol, '')}`;
				} else {
					value = currencySymbol + value.replace(currencySymbol, '');
				}
			}
			return value;
		}
	},
	SEK: {
		locale: 'sv_SE'
	},
	NOK: {
		// show same as sek
		locale: 'sv_SE'
	},
	DKK: {
		locale: 'da_DK',
		postFormat() {
			if (this.value.charAt(this.value.length - 1) === '.') {
				return this.value.slice(0, -1);
			}
			return this.value;
		}
	},
	AUD: {
		locale: 'en_AU'
	},
	CAD: {
		locale: 'en_CA',
		postFormat() {
			const currencySymbol = currencyFormatter.symbols.CAD;
			return this.value.replace(currencySymbol, `C${currencySymbol}`);
		}
	},
	USD: {
		locale: 'en_US'
	},
	$: {
		locale: 'en_US'
	},
	US$: {
		locale: 'en_US'
	},
	NZD: {
		locale: 'en_NZ'
	},
	GBP: {
		locale: 'en_GB'
	},
	CLP: {
		locale: 'en_GB',
		postFormat() {
			const currencySymbol = currencyFormatter.symbols.CLP;
			return this.value.replace(currencySymbol, `CLP${currencySymbol}`);
		}
	},
	PLN: {
		locale: 'pl_PL'
	},
	RUB: {
		locale: 'ru_RU'
		// symbol: 'RUB'
	},
	INR: {
		locale: 'en_IN'
	}
};

/**
 * This function removes the decimals from a currencyformatter-pattern while preserving
 * other properties such as group separator and symbol
 *
 * For the lib currencyformatter.js to remove decimals, a pattern must be sent
 * to the formatter without decimals.
 *
 * Example: `#,##0.00 !` becomes -> `#,##0. !`
 *
 * @param {Object} formatOptions
 */
function getPatternWithoutDecimals(formatOptions) {
	try {
		const details = currencyFormatter.getFormatDetails(formatOptions);

		const { pattern } = details;

		if (!pattern) {
			return undefined;
		}

		const [number, decimals] = pattern.split('.');
		if (!decimals) {
			// The given currency doesn't have any decimals
			return undefined;
		}

		// Remove decimal numbers, they can be written as `0` or `#`
		const newDecimalPatter = decimals.replace(/[#0]+/g, '');
		return `${number}.${newDecimalPatter}`;
	} catch (error) {
		// Just return if there was some problems with the formatDetails
	}
}

/**
 * Returns a locale that our library currencyFormatter can understand
 *
 * @param {string} locale
 */
function getCurrencyFormatterLocale(locale) {
	switch (locale) {
		case 'en_XX':
		case 'en_EU':
			return 'en_GB';
		case 'no_NO':
			return 'nb_NO';
		default:
			return locale;
	}
}

function format2(value, options, hideDecimals = false) {
	// extend options
	const config = {
		value,
		...options,
		...currencyFormatters[options.currency]
	};

	// consolidate options for currency formatter
	const formatOptions = {
		...config,
		...(config.currency && { currency: config.currency }),
		...(config.symbol && { symbol: config.symbol }),
		...(config.locale && {
			locale: getCurrencyFormatterLocale(config.locale)
		})
	};

	if (hideDecimals) {
		const newPattern = getPatternWithoutDecimals(formatOptions);

		if (newPattern) {
			formatOptions.pattern = newPattern;
		}
	}

	try {
		config.value = currencyFormatter.format(value, formatOptions);
	} catch (error) {
		config.value = value.toString();
	}

	if (config.postFormat) {
		config.value = config.postFormat.call(config);
	}

	return config.value.replace(/\s/g, ' ');
}

function format(number, currency, locale = 'sv_SE', hideDecimals = false) {
	let floatNumber = number;
	if (floatNumber === undefined || !currency) {
		return '';
	}

	if (typeof floatNumber === 'string') {
		floatNumber = parseFloat(floatNumber);
	}

	return format2(
		floatNumber,
		{
			currency,
			locale: locale.replace('-', '_').replace('_GU', '')
		},
		hideDecimals
	);
}

export default {
	format
};
